import React, { useState } from "react";
import { Loader2, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { submitLead } from "@/lib/api";
import { trackLeadConversion } from "@/lib/analytics";

export default function Hero() {
    const [form, setForm] = useState({ name: "", mobile: "", course: "" });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const onChange = (k) => (e) =>
        setForm((prev) => ({ ...prev, [k]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.mobile.trim() || !form.course) {
            toast.error("Please Fill All The Fields");
            return;
        }
        const digits = form.mobile.replace(/[^0-9]/g, "");
        if (digits.length < 7) {
            toast.error("Please Enter A Valid Mobile Number");
            return;
        }
        try {
            setSubmitting(true);
            await submitLead({
                name: form.name.trim(),
                mobile: form.mobile.trim(),
                course: form.course,
            });
            trackLeadConversion();
            setSubmitted(true);
            toast.success("Thank You! Our Admissions Team Will Reach Out Shortly.");
            setForm({ name: "", mobile: "", course: "" });
        } catch (err) {
            console.error(err);
            toast.error("Something Went Wrong. Please Try Again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative pt-28 sm:pt-32 md:pt-36 pb-16 sm:pb-20 md:pb-28 overflow-hidden"
            style={{ background: "#2e326b" }}
        >
            {/* Faded campus image overlay */}
            <div
                className="pointer-events-none absolute inset-0 opacity-[0.08] mix-blend-screen"
                style={{
                    backgroundImage:
                        "url('https://images.pexels.com/photos/35564755/pexels-photo-35564755.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600')",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
            />
            {/* Decorative accents */}
            <div
                className="pointer-events-none absolute -top-32 -left-20 w-[420px] h-[420px] rounded-full opacity-40 blur-3xl"
                style={{
                    background:
                        "radial-gradient(circle, rgba(255,192,4,0.55) 0%, transparent 70%)",
                }}
            />
            <div
                className="pointer-events-none absolute -bottom-40 right-0 w-[480px] h-[480px] rounded-full opacity-30 blur-3xl"
                style={{
                    background:
                        "radial-gradient(circle, rgba(255,192,4,0.35) 0%, transparent 70%)",
                }}
            />

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 md:px-10 grid lg:grid-cols-12 gap-10 sm:gap-12 lg:gap-16 items-center">
                {/* LEFT */}
                <div className="lg:col-span-7 animate-fade-up text-white">
                    <div className="inline-flex items-center gap-2 mb-6 sm:mb-7 px-3.5 py-1.5 rounded-full border border-white/20 bg-white/5 backdrop-blur-sm">
                        <span
                            className="h-1.5 w-1.5 rounded-full"
                            style={{ background: "#ffc004" }}
                        />
                        <span
                            className="text-[10px] sm:text-[11px] tracking-[0.2em] uppercase font-semibold"
                            style={{ color: "#ffc004" }}
                        >
                            Admissions Open · Data Science Programs
                        </span>
                    </div>

                    <h1
                        data-testid="hero-headline"
                        className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-[80px] font-black leading-[1.02] tracking-tight"
                        style={{ color: "#ffc004" }}
                    >
                        B.Sc. &amp; M.Sc.
                        <span className="block text-white/95 text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mt-2 sm:mt-3 leading-[1.05]">
                            In Business Analytics &amp; Data Science
                        </span>
                        <span
                            className="block mt-4 sm:mt-5 h-[5px] w-20 sm:w-24"
                            style={{ background: "#ffffff" }}
                        />
                    </h1>

                    <h2
                        data-testid="hero-subheadline"
                        className="mt-6 sm:mt-8 text-xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold leading-tight tracking-tight text-white"
                    >
                        Built On A 70+ Years Legacy Of Excellence
                    </h2>

                    <p
                        data-testid="hero-supporting-text"
                        className="mt-4 sm:mt-5 text-base sm:text-lg max-w-xl leading-relaxed"
                        style={{ color: "rgba(255,255,255,0.78)" }}
                    >
                        Combining Academic Learning With Real-World Exposure
                    </p>

                    <div className="mt-8 sm:mt-10 flex flex-wrap items-center gap-x-5 gap-y-3 text-xs sm:text-sm text-white/80">
                        <div className="flex items-center gap-2">
                            <CheckCircle2
                                className="h-4 w-4"
                                style={{ color: "#ffc004" }}
                            />
                            <span>AICTE Approved</span>
                        </div>
                        <div className="hidden sm:block h-4 w-px bg-white/20" />
                        <div className="flex items-center gap-2">
                            <CheckCircle2
                                className="h-4 w-4"
                                style={{ color: "#ffc004" }}
                            />
                            <span>Worli, South Mumbai</span>
                        </div>
                        <div className="hidden sm:block h-4 w-px bg-white/20" />
                        <div className="flex items-center gap-2">
                            <CheckCircle2
                                className="h-4 w-4"
                                style={{ color: "#ffc004" }}
                            />
                            <span>Industry-Aligned Curriculum</span>
                        </div>
                    </div>
                </div>

                {/* RIGHT - FORM */}
                <div className="lg:col-span-5 animate-fade-up delay-200 w-full">
                    <div
                        id="hero-form"
                        data-testid="hero-form-card"
                        className="relative bg-white rounded-2xl p-6 sm:p-7 md:p-9 shadow-soft border border-white/20"
                    >
                        <div
                            className="absolute -top-3 left-7 sm:left-9 px-3 py-1 rounded-full text-[10px] tracking-[0.18em] uppercase font-bold"
                            style={{ background: "#ffc004", color: "#2e326b" }}
                        >
                            Enquiry Form
                        </div>

                        <h3
                            data-testid="hero-form-heading"
                            className="text-base sm:text-lg md:text-xl font-semibold leading-snug pr-2 sm:pr-4"
                            style={{ color: "#0f172a" }}
                        >
                            Fill In Your Details And Our Admissions Team Will Get In Touch With You.
                        </h3>

                        {submitted ? (
                            <div
                                data-testid="hero-form-success"
                                className="mt-8 flex flex-col items-center text-center py-8"
                            >
                                <div
                                    className="h-14 w-14 rounded-full flex items-center justify-center mb-4"
                                    style={{ background: "rgba(46,50,107,0.08)" }}
                                >
                                    <CheckCircle2
                                        className="h-7 w-7"
                                        style={{ color: "#2e326b" }}
                                    />
                                </div>
                                <p
                                    className="text-lg font-semibold"
                                    style={{ color: "#2e326b" }}
                                >
                                    Thank You!
                                </p>
                                <p className="mt-2 text-sm text-slate-600 max-w-xs">
                                    Our Admissions Team Will Reach Out To You Shortly.
                                </p>
                                <button
                                    type="button"
                                    onClick={() => setSubmitted(false)}
                                    className="mt-6 text-xs tracking-wider uppercase font-semibold underline-offset-4 hover:underline"
                                    style={{ color: "#2e326b" }}
                                    data-testid="hero-form-reset"
                                >
                                    Submit Another Enquiry
                                </button>
                            </div>
                        ) : (
                            <form
                                onSubmit={handleSubmit}
                                className="mt-6 sm:mt-7 space-y-4"
                                data-testid="hero-form"
                                noValidate
                            >
                                <div>
                                    <label className="text-[11px] tracking-[0.14em] uppercase font-semibold text-slate-500 ml-1">
                                        Name
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="Your Full Name"
                                        className="hsnc-input mt-1.5"
                                        value={form.name}
                                        onChange={onChange("name")}
                                        data-testid="hero-form-name"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="text-[11px] tracking-[0.14em] uppercase font-semibold text-slate-500 ml-1">
                                        Mobile Number
                                    </label>
                                    <input
                                        type="tel"
                                        inputMode="tel"
                                        maxLength={18}
                                        placeholder="+91 98765 43210"
                                        className="hsnc-input mt-1.5"
                                        value={form.mobile}
                                        onChange={onChange("mobile")}
                                        data-testid="hero-form-mobile"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="text-[11px] tracking-[0.14em] uppercase font-semibold text-slate-500 ml-1">
                                        Course
                                    </label>
                                    <div className="relative mt-1.5">
                                        <select
                                            value={form.course}
                                            onChange={onChange("course")}
                                            className="hsnc-input appearance-none pr-10"
                                            data-testid="hero-form-course"
                                            required
                                        >
                                            <option value="" disabled>
                                                Select A Program
                                            </option>
                                            <option value="B.Sc.">B.Sc.</option>
                                            <option value="M.Sc.">M.Sc.</option>
                                        </select>
                                        <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-slate-400">
                                            <svg
                                                width="14"
                                                height="14"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <polyline points="6 9 12 15 18 9"></polyline>
                                            </svg>
                                        </div>
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    disabled={submitting}
                                    data-testid="hero-form-submit"
                                    className="group mt-2 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl text-sm font-bold tracking-wide uppercase transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed hover:translate-y-[-1px]"
                                    style={{ background: "#ffc004", color: "#2e326b" }}
                                >
                                    {submitting ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            Submitting
                                        </>
                                    ) : (
                                        <>
                                            Submit
                                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                                        </>
                                    )}
                                </button>

                                <p className="text-[11px] text-slate-400 text-center mt-3 leading-relaxed">
                                    By Submitting, You Agree To Be Contacted By The Hsnc Admissions Team.
                                </p>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}
